import Tippy from '@tippyjs/react';
import React, {
  useState,
  useRef,
  MutableRefObject,
  useImperativeHandle
} from 'react';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import WorklogHoursSection from '@nile/components/src/Containers/NewWorklog/WorklogHoursSection';

const WorklogEditPopupContainer = styled.div`
  min-width: 500px;
`;

interface User {
  userId: string;
  firstName: string;
  lastName: null;
  name: string;
  email: string;
  forgotten: boolean;
}

interface WorkItem {
  id?: string;
  workId?: string;
  module?: 'TICKET' | 'PROJECT' | 'INVOICE' | 'QUOTE';
  subject?: string;
  client?: { name: string; accountId: string };
  requester?: User;
  sop?: { sopId: string; name: string };
  ticketId?: string;
}

interface TimerActionProps {
  getSelectedText: () => string;
  workItem: WorkItem;
}

interface StopwatchResult {
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  reset: (offsetTimestamp?: Date, autoStart?: boolean) => void;
}

interface TimerObject {
  timerId?: string;
  billable?: boolean;
  serviceItem?: {
    itemId: string;
    name?: string;
  };
  notes?: string;
  state?: 'RUNNING' | 'PAUSED';
  type?: 'AUTOMATIC' | 'MANUAL';
  timespent?: number;
  workItem?: WorkItem;
  running?: boolean;
  createdTime?: string;
  module?: string;
  technician?: User;
  disableSaveTimer?: boolean;
}

export const TimerPopup = (props): React.ReactNode => {
  const {
    refFromProps,
    hideWorkitem,
    associateWorkItem,
    handleAssociateWorkItem,
    handleParentClose,
    handleOutsideClick,
    handleWorklogCreated,
    isAgentChat = false,
    threadData,
    disableSaveTimer,
    hideDelete
  } = props;
  const popupParentName = 'EditTimerParent';
  const editTippyRef = useRef();
  let timeout = null;
  const [timer, setTimer] = useState<TimerObject>(null);
  const [manualMode, setManualMode] = useState<boolean>(false);
  const [editMounted, setEditMounted] = useState(false);
  const [aiWorklogNotes, setAIWorklogNotes] = useState<boolean>(false);
  const [timerNote, setTimerNote] = useState<String>('');
  const [disableSaveTimerState, setDisableSaveTimerState] = useState<boolean>(
    disableSaveTimer
  );

  const closeAllOpenPopups = () => {
    if (
      document.getElementsByClassName(popupParentName) &&
      document.getElementsByClassName(popupParentName)?.length
    ) {
      Array.prototype.forEach.call(
        document.getElementsByClassName(popupParentName),
        (popup) => {
          if (popup?._tippy.state.isVisible) {
            popup?._tippy.hide();
          }
        }
      );
    }
  };

  function closePopover() {
    setTimeout(() => {
      setEditMounted(false);
      editTippyRef.current._tippy.hide();
      // document.removeEventListener('click', handleClickOutside);
    }, 50);
  }

  function handleClickOutside(e) {
    if (handleOutsideClick) {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        const clickedOutside = [];
        ['EditTimerParent', 'AssociateWorkItemParent'].forEach((pName) => {
          document.getElementsByClassName(pName).forEach((popup) => {
            if (
              popup?._tippy?.state?.isVisible &&
              popup._tippy.popper.contains(e.target)
            ) {
              clickedOutside.push(true);
            }
          });
        });
        if (!clickedOutside.some((res) => res)) {
          closePopover();
        }
      }, 20);
    }
  }

  const toggleEditPopover = ({
    currentTimer,
    aiWorklogNotes,
    onlyManual = false,
    timerNote
  }: {
    currentTimer: TimerObject;
    onlyManual: boolean;
    aiWorklogNotes: boolean;
    timerNote: String;
  }): void => {
    if (currentTimer) setTimer(currentTimer);
    if (aiWorklogNotes) {
      setAIWorklogNotes(true);
    }
    if (timerNote) {
      setTimerNote(timerNote);
    }
    if (currentTimer?.disableSaveTimer) {
      setDisableSaveTimerState(currentTimer?.disableSaveTimer);
    }
    setManualMode(onlyManual);
    if (editTippyRef?.current?._tippy) {
      if (!editTippyRef.current._tippy.state?.isVisible) {
        setEditMounted(true);
        if (props?.closeAllOpenPopups) {
          props?.closeAllOpenPopups();
        } else {
          closeAllOpenPopups();
        }
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          editTippyRef.current._tippy.popperInstance.forceUpdate();
        }, 2000);
        // document.addEventListener('click', handleClickOutside);
        editTippyRef.current._tippy.show();
      } else if (editTippyRef.current._tippy.state?.isVisible) {
        closePopover();
      }
    }
  };

  function repositionPopup() {
    if (editTippyRef.current?._tippy?.popperInstance?.forceUpdate) {
      editTippyRef.current._tippy.popperInstance.forceUpdate();
    }
  }

  interface PopoverActions {
    togglePopover: ({ currentTimer, onlyManual }) => void;
  }

  useImperativeHandle(
    refFromProps,
    (): PopoverActions => {
      return {
        togglePopover: toggleEditPopover
      };
    }
  );

  const autoFocusTimerNotes = props?.autoFocusTimerNotes || aiWorklogNotes;

  return (
    <Tippy
      content={
        editMounted ? (
          <WorklogEditPopupContainer
            onChange={repositionPopup}
            key={timer?.timerId}
          >
            <WorklogHoursSection
              editMode
              timer={timer}
              onlyManual={!!manualMode}
              hideDelete={hideDelete}
              associateWorkItem={associateWorkItem}
              handleAssociateWorkItem={handleAssociateWorkItem}
              workItem={timer?.workItem}
              handleClose={closePopover}
              handleWorklogCreated={handleWorklogCreated}
              handleParentClose={handleParentClose}
              module={timer?.module || null}
              repositionPopup={repositionPopup}
              hideWorkitem={hideWorkitem}
              threadData={threadData}
              disableCloseOnSuccess={isAgentChat}
              disableAutoTimerCloseOnSuccess={aiWorklogNotes || isAgentChat}
              closeOnCancel
              aiWorklogNotes={aiWorklogNotes} // Boolean for AI worklog note
              timerNote={timerNote} // We'll pass the selected text her
              disableSaveTimer={disableSaveTimerState}
              isAgentChat={isAgentChat}
              disableRunningTimerCloseOnSuccess={aiWorklogNotes}
              autoFocusTimerNotes={autoFocusTimerNotes}
            />
          </WorklogEditPopupContainer>
        ) : null
      }
      trigger="manual" // manually close the tip on api calls
      interactive
      placement="right"
      arrow={false}
      theme="light"
      allowHTML
      ref={(node) => {
        editTippyRef.current = node;
      }}
      appendTo={document.body}
      offset={[-150, 30]}
      flipOnUpdate
      onShow={repositionPopup}
      // onHide={() => {
      //   setEditMounted(false);
      // }}
      hideOnClick="toggle"
      maxWidth={500}
      zIndex={510}
    >
      {props?.children}
    </Tippy>
  );
};

export default TimerPopup;
